'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { isPrototypeLifecycle } from '@/utils/product-lifecycle';
import saveAs from 'file-saver';
import JSZip from 'jszip';
import Link from '@/components/atoms/link';
import { CurrencyHelpers } from '@/helpers/currencyHelpers';
import Image from '@/components/atoms/project-builder/image';
import {
  THUMBNAIL, GENERIC_MEDIA, TECH_IMAGE, TECH_MANUAL, TECH_MANUAL_SET,
  INSTALLATION_GUIDELINES, INSTALLATION_GUIDELINES_SET,
  PRODUCT_TYPE, RANGE, COLOR, ITEM_MATERIAL, COATING_METHOD, BIM_FILE_TYPES
} from '@/helpers/constants/attributes';
import VariantAttributesList from './variantAttributesList';
import { typeSelectDataForPublishedProducts } from '@/helpers/server/published-products-select-data';
import { useParams } from 'next/navigation';
import { localeMapping } from '@/helpers/constants/localeMapping';
import { checkIsBuilderProduct } from '@/helpers/builder-product-helper';

const EXCLUDED_MEDIA = [TECH_IMAGE, TECH_MANUAL, TECH_MANUAL_SET, INSTALLATION_GUIDELINES, INSTALLATION_GUIDELINES_SET];
interface FlatItem {
  item: any;
  rowIndex: number;
  roomName: string;
}
const renameMap: Record<string, string> = {
  IsAS14281Compliant: 'AS1428.1 Accessible',
  IsAS14281Ambulant: 'AS1428.1 Ambulant',
  IsIndependentLivingCompliant: 'Independent Living Compliant',
  IsNZ41121Compliant: 'NZ412.1 Accessible',
};

const getAddlAttributes = (locale: string, productCategory: string, productSubCategory: string, attributes: any) => {
  return attributes.filter((attr: any) => {
    if (['depth', 'length', 'width', 'net-weight', 'IsIndependentLivingCompliant'].includes(attr.name)) return true;
    if (['IsAS14281Compliant', 'IsAS14281Ambulant'].includes(attr.name) && locale === 'au') return true;
    if (['IsNZ41121Compliant'].includes(attr.name) && locale === 'nz') return true;
  }).map((attr: any, index: number) => (
    <React.Fragment key={index}>
      {(
        (
          ((attr.name === 'IsAS14281Compliant' || attr.name === 'IsNZ41121Compliant' || attr.name === 'IsIndependentLivingCompliant') && (
            ['sanitaryware', 'tapware', 'showers', 'bathroom-accessories', 'basins',
              'laundry-tubs'].includes(productCategory) ||
            (productCategory == 'kitchen-sinks-accessories' && productSubCategory == 'kitchen-accessories') ||
            (productCategory == 'kitchen-sinks-accessories' && ['sinks', 'care-sinks'].includes(productSubCategory))
          )) ||
          (attr.name === 'IsAS14281Ambulant' && (
            ['sanitaryware', 'bathroom-accessories'].includes(productCategory) ||
            (productCategory == 'kitchen-sinks-accessories' && productSubCategory == 'kitchen-accessories')
          )) ||
          !['IsAS14281Compliant', 'IsAS14281Ambulant', 'IsIndependentLivingCompliant', 'IsNZ41121Compliant'].includes(attr.name)
        )
        && (
          <div key={index} className="flex flex-col gap-[8px]">
            <div className="flex text-sm font-normal leading-6 print:text-xs lg:text-sm">
              <span className="flex-1 capitalize text-[#4D4D4D] text-[14px] ">{(renameMap[attr.name] || attr.name).replace(/-/g, ' ')}</span>
              <span className="ml-[20px] flex-1 text-[#090909]  text-[14px] print:ml-0 print:w-1/2">{typeof attr.value === 'boolean' ? (attr.value ? 'Yes' : 'No') : attr.value}</span>
            </div>
            <div className="border-b border-[#666666] print:border-[1px]" />
          </div>
        )
      )}
    </React.Fragment>
  ));
}

const getPrimaryAttributes = (attributes: Record<string, any>) =>
  [PRODUCT_TYPE, RANGE, COLOR, ITEM_MATERIAL, COATING_METHOD].map((key) => {
    const value = attributes?.[key];
    if (!value || value === 'Not Applicable') return null;

    const display = Array.isArray(value)
      ? value.join(', ')
      : typeof value === 'boolean'
        ? value ? 'Yes' : 'No'
        : value;

    return (
      <div key={key} className="flex flex-col gap-[8px]">
        <div className="flex text-sm font-normal leading-6 print:text-xs lg:text-sm">
          <span className="flex-1 capitalize text-[#4D4D4D]">{key.replace(/-/g, ' ')}</span>
          <span className="ml-[20px] flex-1 text-[#090909] print:ml-0 print:w-1/2">{display}</span>
        </div>
        <div className="border-b border-[#666666] print:border-[1px]" />
      </div>
    );
  });

const ProductImage = React.memo(({ src }: { src: string; }) => (
  <div className="relative h-[136px] w-full max-w-[140px]">
    <Image fill src={src || THUMBNAIL} style={{ objectFit: 'contain' }} loading="lazy" />
  </div>
));

const MediaLinks = React.memo(({ mediaAssets }: { mediaAssets: any[]; }) => (
  <div className="flex flex-col gap-y-[5px] py-[7px] print:py-2">
    {mediaAssets.length > 0 ? mediaAssets.map((file) => (
      <Link
        key={file.name}
        link={file.value}
        download
        target="_blank"
        className="w-full border-b border-[#666] print:boder-[0.5px]"
      >
        <div className="cursor-pointer pb-2 text-sm font-medium text-[#666] underline print:pb-2 print:text-xs">
          {file.name.replace('-file', '')}
        </div>
      </Link>
    )) : (
      <div className="text-left text-sm font-medium text-[#666] print:text-xs">N/A</div>
    )}
  </div>
));

const SummaryTable = ({ roomGroups, showPricesOnPdf, setLoading, selectDataForPublishedProducts }: any) => {
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  const selectDataForPublishedProductsLocaleSpecific =
    locale === 'au' ? (selectDataForPublishedProducts as typeSelectDataForPublishedProducts)?.dataAU :
      locale === 'au' ? (selectDataForPublishedProducts as typeSelectDataForPublishedProducts)?.dataAU :
        [];
  const [errorMessage, setErrorMessage] = useState('');
  const [errorIndex, setErrorIndex] = useState(-1);

  const headers = useMemo(() => {
    const base = ['Room', 'Image', 'Prod Code', 'Product Name', 'Specification/Details', 'Bim Files', 'Qty'];
    if (showPricesOnPdf) base.push('Price');
    return base;
  }, [showPricesOnPdf]);

  const flatItems: FlatItem[] = useMemo(() => (
    roomGroups?.flatMap((room: any) =>
      room.items?.filter((item: any) => item.isProductAvailable).map((item: any, idx: number) => ({
        item,
        rowIndex: idx,
        roomName: room.roomName,
      }))
    ) || []
  ), [roomGroups]);

  const totalQuantity = useMemo(() => flatItems.reduce((sum, { item }) => sum + item.quantity, 0), [flatItems]);
  const totalAmount = useMemo(() =>
    flatItems.reduce((sum, { item }) => {
      const itemIsPrototype = isPrototypeLifecycle(
        item.variant?.attributes?.['item-attributes']?.find((attr: any) => attr.name === 'lifecycle')?.value
      );
      const isBuilderProduct = checkIsBuilderProduct(item, selectDataForPublishedProducts, (locale as string) || 'au');
      return sum + (itemIsPrototype || isBuilderProduct ? 0 : item.quantity * item?.variant?.price?.centAmount);
    }, 0), [flatItems, selectDataForPublishedProducts, locale]);

  const getFilename = (url: string) => url.split('/').pop() || 'file';

  const handleDownloadAllFiles = useCallback(async (item: any, rowIndex: number) => {
    const zip = new JSZip();
    const media = item?.variant?.attributes?.[GENERIC_MEDIA] ?? [];
    const urls = media.flatMap((file: any) =>
      (Array.isArray(file?.value) ? file.value : [file?.value]).filter(Boolean)
    );

    try {
      setLoading(true);
      for (let i = 0; i < urls.length; i++) {
        const originalUrl = urls[i].replaceAll('ProductThumbnail', 'Original');
        const urlWithCache = `${originalUrl}?cacheBust=${Date.now()}_${i}`;
        const response = await fetch(urlWithCache);
        if (!response.ok) throw new Error('Failed to fetch file');
        const blob = await response.blob();
        zip.file(getFilename(originalUrl), blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'files.zip');
    } catch (err) {
      console.error(err);
      setErrorMessage('Error downloading files. Try again later.');
      setErrorIndex(rowIndex);
      setTimeout(() => setErrorMessage(''), 2000);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  return (
    <div className="mt-4 overflow-x-auto print:mt-1">
      <table className="w-full text-[14px] border border-black border-collapse rounded-[1px]" style={{ border: '1px' }}>
        <thead>
          <tr className="bg-[#E7E5EB]">
            {headers.map((h, idx) => (
              <th key={idx} className="border border-black p-[8px] text-left font-normal print:font-semibold print:p-[8px] print:text-xs">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="print:text-xs">
          {flatItems.map(({ item, roomName, rowIndex }) => {
            const attributes = item?.variant?.attributes || {};
            const mediaAssets = attributes['assets-attributes']?.filter(
              (a: any) => !EXCLUDED_MEDIA.includes(a.name) && !!a.value
            ) || [];
            const parentProduct = selectDataForPublishedProductsLocaleSpecific?.find((element: any) =>
              element.variants.some((variant: any) => variant.key === attributes['item-code'])
            );
            const productCategory = parentProduct?.categoryKeys?.[localeKey]?.[0]?.toLowerCase().replace(/\s+/g, '-');
            const productSubCategory = parentProduct?.categoryKeys?.[localeKey]?.[1]?.toLowerCase().replace(/\s+/g, '-');
            const technicalImage = attributes['assets-attributes']?.find((attr: any) => attr.name === 'technical-image')?.value;
            return (
              <tr key={`${roomName}-${rowIndex}`} className="border border-black bg-[#F7F7F7] align-top">
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">{rowIndex === 0 ? roomName : ''}</td>
                <td className="border border-black w-1/5 px-2 py-2 print:p-[8px] ">
                <div className='flex flex-col gap-2 print:gap-2'>
                  <ProductImage src={item?.variant?.images?.[0]} />
                  {technicalImage && (
                    <ProductImage src={technicalImage} />
                  )}
                </div>
                </td>
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563] font-semibold">{attributes['item-code']}</td>
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">{item.variant.attributes["item-name"]}</td>
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">
                  {getPrimaryAttributes(attributes)}
                  <VariantAttributesList attributes={attributes} />
                  {getAddlAttributes(locale, productCategory, productSubCategory, attributes['item-attributes'])}
                </td>
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">
                  <div className="flex flex-col gap-10">
                    <MediaLinks mediaAssets={mediaAssets} />
                    {mediaAssets.length > 0 && (
                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={() => handleDownloadAllFiles(item, rowIndex)}
                          disabled={
                            item?.variant?.attributes?.[BIM_FILE_TYPES]?.filter(
                              (b: string) => b.toLowerCase() !== 'bom',
                            )?.length === 0
                          }
                          className="cursor-pointer pb-2.5 text-left text-sm font-medium text-[#666] underline disabled:opacity-50 print:hidden print:pb-2 print:text-xs"
                        >
                          Download all
                        </button>
                        {errorMessage && errorIndex === rowIndex && (
                          <div className="mt-5 pb-2 text-sm font-medium text-red-500 print:text-xs">
                            {errorMessage}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">{item.quantity}</td>
                {showPricesOnPdf && (() => {
                  const itemIsPrototype = isPrototypeLifecycle(
                    item.variant?.attributes?.['item-attributes']?.find((attr: any) => attr.name === 'lifecycle')?.value
                  );
                  const isBuilderProduct = checkIsBuilderProduct(item, selectDataForPublishedProducts, (locale as string) || 'au');
                  return (
                    <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">
                      {!itemIsPrototype && !isBuilderProduct
                        ? CurrencyHelpers.formatForCurrency(
                            item.quantity * item?.variant?.price?.centAmount,
                            'en', 'USD', 2
                          ).replace('US', '')
                        : ''}
                    </td>
                  );
                })()}
              </tr>
            );
          })}
          <tr className="border border-black">
            <td className="border border-black px-[8px] py-[8px] text-right font-semibold print:p-[8px] text-[#4B5563] " colSpan={6}>
              Total
            </td>
            <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">{totalQuantity}</td>
            {showPricesOnPdf && (
              <td className="border border-black px-[8px] py-[8px] print:p-[8px] text-[#4B5563]">
                {CurrencyHelpers.formatForCurrency(totalAmount, 'en', 'USD', 2).replace('US', '')}
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default SummaryTable;
